import { CareerProfileInput } from 'core';
import { ICareerProfileFormValues } from 'organisms';

import { monthNameToNumber, yearToNumber } from './monthBridge';

/**
 * Maps the editor's view model into `CareerProfileInput` (spec 0018, increment B).
 *
 * Delete-and-recreate semantics for skills / projects / experience / education /
 * languages: the FULL collections are always sent. Empty rows are dropped so the
 * backend doesn't recreate blank entries. A "current" job never sends an end date.
 *
 * Certificates are reconciled by id instead (spec 0018 §9): an existing row sends
 * its `serverId` as `id` (update in place, preserving an admin-uploaded file); a new
 * row omits `id` (create); a removed card's id is simply absent (delete).
 */
export const transformProfileToInput = (values: ICareerProfileFormValues): CareerProfileInput => ({
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  phone: values.phone,
  city: values.city,
  country: values.country,
  relocationReady: values.relocationReady,
  workMode: values.workMode || null,
  workPermit: values.workPermit,
  availableFrom: values.availableFrom || null,
  seniority: values.seniority || null,
  desiredPosition: values.desiredPosition,
  desiredSalary: values.desiredSalary,
  githubUrl: values.githubUrl,
  linkedinUrl: values.linkedinUrl,
  portfolioUrl: values.portfolioUrl,
  customSkills: values.customSkills,
  skills: values.skills.map((skill) => ({
    skillId: skill.skillId,
    level: skill.level,
    // ТЗ 0039. `source` НЕ отправляем: его нет во входном типе, и это
    // намеренно — подтверждённость ставит только сервер.
    isHidden: skill.isHidden,
    years: skill.years ?? undefined,
  })),
  projects: values.projects
    .filter((project) => project.title.trim())
    .map((project) => ({
      title: project.title,
      description: project.description,
      role: project.role,
      repoUrl: project.repoUrl,
      demoUrl: project.demoUrl,
      technologyIds: project.technologies.map((tech) => tech.id),
    })),
  experience: values.experience
    .filter((item) => item.company.trim() || item.position.trim())
    .map((item) => ({
      company: item.company,
      position: item.position,
      description: item.description,
      startYear: yearToNumber(item.startDate.year),
      startMonth: monthNameToNumber(item.startDate.month),
      endYear: item.isCurrent ? null : yearToNumber(item.endDate.year),
      endMonth: item.isCurrent ? null : monthNameToNumber(item.endDate.month),
      isCurrent: item.isCurrent,
    })),
  education: values.education
    .filter((item) => item.institution.trim() || item.degree.trim() || item.specialization.trim())
    .map((item) => ({
      institution: item.institution,
      degree: item.degree,
      specialization: item.specialization,
      startYear: yearToNumber(item.startDate.year),
      startMonth: monthNameToNumber(item.startDate.month),
      endYear: yearToNumber(item.endDate.year),
      endMonth: monthNameToNumber(item.endDate.month),
    })),
  languages: values.languages
    .filter((item) => !!item.language.trim() && !!item.level)
    .map((item) => ({
      language: item.language,
      level: item.level || undefined,
    })),
  certificates: values.certificates
    .filter((item) => item.title.trim())
    .map((item) => ({
      id: item.serverId ?? undefined,
      title: item.title,
      issuer: item.issuer,
      issuedAt: item.issuedAt || null,
      url: item.url,
    })),
});
